import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Boxes, Pencil, Power, Trash2 } from "lucide-react";

import { PageHeader } from "@/components/common/PageHeader";
import { DataTable } from "@/components/common/DataTable";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { RoleGate } from "@/components/auth/RoleGate";
import { assetsService } from "@/services/assets.service";
import { getErrorMessage } from "@/services/api";
import {
    ASSET_CATEGORIES,
    OPERATIONAL_STATUS_OPTIONS,
    OPERATIONAL_STATUS_LABELS,
} from "@/config/constants";
import { formatDate } from "@/lib/utils";
import { usePermission } from "@/hooks/usePermission";

export default function AssetsListPage() {
    const navigate = useNavigate();
    const qc = useQueryClient();
    const { can } = usePermission();

    const [page, setPage] = useState(1);
    const [search, setSearch] = useState("");
    const [category, setCategory] = useState("");
    const [operationalStatus, setOperationalStatus] = useState("");
    const [sort, setSort] = useState({ sortBy: "created_at", sortOrder: "desc" });
    const [confirm, setConfirm] = useState(null);

    const query = useQuery({
        queryKey: ["assets", { page, search, category, operationalStatus, ...sort }],
        queryFn: () =>
            assetsService.list({
                page, pageSize: 20, search,
                sortBy: sort.sortBy, sortOrder: sort.sortOrder,
                category: category || undefined,
                operational_status: operationalStatus || undefined,
            }),
    });

    const del = useMutation({
        mutationFn: (id) => assetsService.remove(id),
        onSuccess: () => {
            toast.success("Equipamento removido.");
            qc.invalidateQueries({ queryKey: ["assets"] });
        },
        onError: (e) => toast.error(getErrorMessage(e, "Falha ao remover equipamento.")),
    });

    const columns = [
        {
            key: "internal_code",
            header: "Código",
            sortable: true,
            cell: (r) => (
                <div className="flex items-center gap-3">
                    <div className="grid h-8 w-8 place-items-center rounded-md bg-muted text-muted-foreground">
                        <Boxes className="h-4 w-4" />
                    </div>
                    <div className="min-w-0">
                        <div className="font-mono text-sm font-medium">{r.internal_code}</div>
                        <div className="truncate text-xs text-muted-foreground">
                            {r.manufacturer || "—"} {r.model ? `· ${r.model}` : ""}
                        </div>
                    </div>
                </div>
            ),
        },
        {
            key: "category",
            header: "Categoria",
            cell: (r) => <Badge variant="outline" className="text-[10px]">{r.category || "—"}</Badge>,
        },
        { key: "serial_number", header: "Núm. de Série", cell: (r) => r.serial_number || "—" },
        { key: "customer_asset_tag", header: "Patrimônio", cell: (r) => r.customer_asset_tag || "—" },
        {
            key: "operational_status",
            header: "Situação",
            cell: (r) => {
                const s = r.operational_status || "operante";
                return s === "operante"
                    ? <Badge variant="outline" className="border-success/40 text-success">Operante</Badge>
                    : <Badge variant="outline" className="border-danger/40 text-danger"><Power className="mr-1 h-3 w-3" />Parado</Badge>;
            },
        },
        {
            key: "created_at",
            header: "Cadastro",
            sortable: true,
            cell: (r) => <span className="text-xs text-muted-foreground">{formatDate(r.created_at)}</span>,
        },
        {
            key: "actions", header: "", className: "w-24",
            cell: (r) => (
                <div className="flex justify-end gap-1">
                    <RoleGate resource="assets" action="update">
                        <Button variant="ghost" size="icon" className="h-8 w-8"
                            onClick={(e) => { e.stopPropagation(); navigate(`/assets/${r.id}/edit`); }}
                            data-testid={`assets-edit-${r.id}`}>
                            <Pencil className="h-4 w-4" />
                        </Button>
                    </RoleGate>
                    <RoleGate resource="assets" action="delete">
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-danger hover:text-danger"
                            onClick={(e) => { e.stopPropagation(); setConfirm(r); }}
                            data-testid={`assets-delete-${r.id}`}>
                            <Trash2 className="h-4 w-4" />
                        </Button>
                    </RoleGate>
                </div>
            ),
        },
    ];

    const filtersActive = [];
    if (category) filtersActive.push(`Categoria: ${category}`);
    if (operationalStatus) filtersActive.push(`Situação: ${OPERATIONAL_STATUS_LABELS[operationalStatus]}`);

    return (
        <div className="space-y-6">
            <PageHeader
                title="Equipamentos"
                description="Cadastro e histórico completo dos equipamentos dos clientes."
                breadcrumb={[{ label: "Início", to: "/dashboard" }, { label: "Equipamentos" }]}
            />
            <DataTable
                dataTestId="assets"
                columns={columns}
                query={query.data}
                loading={query.isLoading}
                onSearch={(t) => { setPage(1); setSearch(t); }}
                onPageChange={setPage}
                onSortChange={setSort}
                onCreate={can("assets", "create") ? () => navigate("/assets/new") : null}
                createLabel="Novo equipamento"
                onRowClick={(r) => navigate(`/assets/${r.id}`)}
                emptyState={{
                    title: "Sem equipamentos",
                    description: "Cadastre o primeiro equipamento.",
                    cta: "Novo equipamento",
                }}
                initialSort={sort}
                filtersActive={filtersActive}
                searchPlaceholder="Buscar por código, série, patrimônio, fabricante, modelo…"
                toolbarLeft={
                    <>
                        <Select value={category || "__all__"}
                            onValueChange={(v) => { setPage(1); setCategory(v === "__all__" ? "" : v); }}>
                            <SelectTrigger className="h-9 w-40" data-testid="assets-category-filter">
                                <SelectValue placeholder="Categoria" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="__all__">Todas categorias</SelectItem>
                                {ASSET_CATEGORIES.map((c) => (
                                    <SelectItem key={c} value={c}>{c}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <Select value={operationalStatus || "__all__"}
                            onValueChange={(v) => { setPage(1); setOperationalStatus(v === "__all__" ? "" : v); }}>
                            <SelectTrigger className="h-9 w-36" data-testid="assets-op-status-filter">
                                <SelectValue placeholder="Situação" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="__all__">Todas situações</SelectItem>
                                {OPERATIONAL_STATUS_OPTIONS.map((s) => (
                                    <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </>
                }
            />
            <ConfirmDialog
                open={!!confirm}
                onOpenChange={(open) => !open && setConfirm(null)}
                title="Excluir equipamento?"
                description={`\"${confirm?.internal_code || ""}\" será removido permanentemente.`}
                confirmLabel="Excluir" variant="destructive"
                onConfirm={() => { if (confirm) del.mutate(confirm.id); setConfirm(null); }}
            />
        </div>
    );
}
